import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ExternalLink, Check, Loader2, CalendarPlus } from "lucide-react";
import { toast } from "sonner";
import { isStripeTestMode } from "@/config/stripePlans";
import { BookingAccessRecovery } from "@/components/booking/BookingAccessRecovery";

interface BookingData {
  booking_id: string;
  booking_date: string;
  booking_time: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string | null;
  number_of_pax: number | null;
  short_id: string | null;
  listing_id: string | null;
  series_id: string | null;
  series_position: number | null;
  end_time: string | null;
  resource_id: string | null;
  resources: { name: string } | null;
  listings: {
    name: string;
    description: string | null;
    images: string[] | null;
    business_id: string;
    booking_mode: string | null;
    duration_minutes: number | null;
    businesses: {
      slug: string | null;
    } | null;
  } | null;
}

interface SeriesOccurrence {
  booking_date: string;
  series_position: number;
}

const ReadOnlyField = ({
  label,
  value,
}: {
  label: string;
  value: string | number;
}) => (
  <div className="space-y-1.5">
    <Label>{label}</Label>
    <Input value={value} readOnly disabled />
  </div>
);

const BookingConfirmed = () => {
  const navigate = useNavigate();
  const { bookingId } = useParams<{ bookingId: string }>();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const urlAccessToken = searchParams.get("token") ?? "";

  const [booking, setBooking] = useState<BookingData | null>(null);
  const [accessToken, setAccessToken] = useState(urlAccessToken);
  const [seriesOccurrences, setSeriesOccurrences] = useState<SeriesOccurrence[]>([]);
  const [seriesBillingMode, setSeriesBillingMode] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadBooking = async () => {
      try {
        let resolvedBookingId = bookingId;
        let resolvedAccessToken = urlAccessToken;

        // Stripe flow: verify session and create booking
        if (sessionId && !bookingId) {
          // Read the stripeConnectId passed via the URL by ListingBooking.tsx
          const accountId = searchParams.get("account");

          if (!accountId) {
            throw new Error("Could not find Stripe configuration");
          }

          const { data: verifyData, error: verifyError } = await supabase.functions.invoke(
            "stripe-verify-session",
            {
              body: {
                sessionId,
                stripeConnectId: accountId,
                test_mode: isStripeTestMode,
              },
            }
          );

          if (verifyError || !verifyData?.success) {
            // Payment failed or session invalid
            toast.error(
              verifyData?.error || "Stripe payment was not successful. Please try again or choose another payment method."
            );
            navigate(-1);
            return;
          }

          resolvedBookingId = verifyData.bookingId;
          resolvedAccessToken = verifyData.accessToken;
        }

        if (!resolvedBookingId || !resolvedAccessToken) {
          throw new Error("Secure booking access is required");
        }

        // Fetch the booking (with listing data) through the manage-booking fn.
        // The bookings table is no longer publicly readable.
        const { data: lookup, error: fetchError } = await supabase.functions.invoke(
          "manage-booking",
          { body: { action: "get", access_token: resolvedAccessToken } }
        );

        if (fetchError || !lookup?.booking) {
          throw new Error("Booking not found");
        }
        const data = lookup.booking;

        setAccessToken(resolvedAccessToken);
        setBooking(data as unknown as BookingData);

        // Series. Sibling dates so the customer sees the full schedule
        // they just signed up for.
        if (lookup.series?.occurrences?.length) {
          setSeriesOccurrences(lookup.series.occurrences as SeriesOccurrence[]);
          setSeriesBillingMode((lookup.series.billing_mode as string | null) ?? null);
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Failed to load booking";
        setError(msg);
        console.error("Error loading booking:", err);
      } finally {
        setLoading(false);
      }
    };

    loadBooking();
  }, [bookingId, sessionId, navigate, urlAccessToken, searchParams]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Confirming your booking...</p>
        </div>
      </div>
    );
  }

  if (error || !booking) {
    return <BookingAccessRecovery bookingId={bookingId} />;
  }

  const listing = booking.listings;
  const imageUrl = listing?.images && listing.images.length > 0 ? listing.images[0] : null;

  // Format date for display (dd/MM/yyyy)
  const formatDisplayDate = (dateStr: string) => {
    const parts = dateStr.split("-");
    if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
    return dateStr;
  };

  // Build external listing link
  const businessSlug = listing?.businesses?.slug;
  const listingLink = booking.listing_id
    ? `/listing/${booking.listing_id}`
    : "#";

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-background/95 backdrop-blur-sm border-b">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-center">
          <div className="flex items-center gap-2">
            <img
              src="/dibs_assets/red_logo_with_transparent_bg.png"
              alt="Dibs"
              className="h-8 object-contain"
            />
            <span className="text-primary font-sans font-bold text-2xl tracking-tight">Dibs</span>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8 md:py-12 flex flex-col gap-6">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-green-500/10 flex items-center justify-center shrink-0">
              <Check className="w-5 h-5 text-green-600" />
            </div>
            <h1 className="text-2xl font-heading font-bold text-foreground leading-none">Booking confirmed!</h1>
          </div>
          <p className="text-muted-foreground mt-1 text-sm">Your booking has been successfully placed. We look forward to seeing you.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 items-start">

          {/* LEFT: Listing Card */}
          <div className="md:col-span-2 border rounded-xl shadow-sm bg-card overflow-hidden">
            {imageUrl ? (
              <div className="aspect-video w-full bg-muted">
                <img src={imageUrl} alt={listing?.name || "Listing"} className="w-full h-full object-cover" />
              </div>
            ) : (
              <div className="aspect-video w-full bg-muted flex items-center justify-center">
                <span className="text-muted-foreground text-sm">No image available</span>
              </div>
            )}

            <div className="p-5 flex flex-col gap-3">
              <div className="flex items-start justify-between gap-3">
                <h2 className="text-lg font-bold leading-tight flex-1">{listing?.name || "Booking"}</h2>
                <Link
                  to={listingLink}
                  className="shrink-0 p-1.5 bg-muted/50 rounded-md hover:bg-muted text-muted-foreground hover:text-primary transition-colors"
                  aria-label="Open listing in new window"
                >
                  <ExternalLink className="w-4 h-4" />
                </Link>
              </div>

              {listing?.description && (
                <p className="text-sm text-muted-foreground line-clamp-4">
                  {listing.description}
                </p>
              )}
            </div>
          </div>

          {/* RIGHT: Booking Details */}
          <div className="md:col-span-3 border rounded-xl shadow-xl bg-card overflow-hidden flex flex-col">
            <div className="p-6 space-y-6 flex-1">
              {/* Schedule Display */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">Schedule</h3>

                <div className="grid gap-4">
                  <div className="space-y-1.5">
                    <Label>Date</Label>
                    <Input value={formatDisplayDate(booking.booking_date)} readOnly disabled />
                  </div>

                  <div className="space-y-1.5">
                    <Label>Time</Label>
                    <Input
                      value={
                        booking.listings?.booking_mode === "appointment" && booking.listings?.duration_minutes
                          ? `${booking.booking_time} (${booking.listings.duration_minutes} min)`
                          : booking.booking_time
                      }
                      readOnly
                      disabled
                    />
                  </div>

                  {booking.resources?.name && (
                    <div className="space-y-1.5">
                      <Label>With</Label>
                      <Input value={booking.resources.name} readOnly disabled />
                    </div>
                  )}
                </div>
              </div>

              {/* Series schedule */}
              {booking.series_id && seriesOccurrences.length > 1 && (
                <div className="space-y-3 rounded-lg border border-primary/20 bg-primary/5 p-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium uppercase tracking-wider text-primary">
                      Recurring series · {seriesOccurrences.length} sessions
                    </h3>
                  </div>
                  <ul className="space-y-1.5">
                    {seriesOccurrences.map((o) => (
                      <li key={o.series_position} className="flex items-center justify-between text-sm">
                        <span className="text-foreground">
                          {o.series_position}. {formatDisplayDate(o.booking_date)} · {booking.booking_time}
                        </span>
                        {o.series_position === 1 && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/10 text-green-700 font-medium">First session</span>
                        )}
                      </li>
                    ))}
                  </ul>
                  <p className="text-xs text-muted-foreground">
                    {seriesBillingMode === "upfront"
                      ? `All ${seriesOccurrences.length} sessions are reserved together and paid for as one programme. Cancel a session up to 24 hours before its slot — any refund follows the centre's refund policy.`
                      : "Cancel any single session up to 24 hours before its slot for a refund. For online payments, future sessions are auto-charged 24 hours before each slot."}
                  </p>
                </div>
              )}

              {/* Guest Information Display */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">Guest information</h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  <ReadOnlyField
                    label="Name"
                    value={booking.customer_name}
                  />
                  <ReadOnlyField
                    label="Pax"
                    value={booking.number_of_pax || 1}
                  />
                  <ReadOnlyField
                    label="Email"
                    value={booking.customer_email}
                  />
                  {booking.customer_phone && (
                    <ReadOnlyField
                      label="Phone"
                      value={booking.customer_phone}
                    />
                  )}
                </div>
              </div>
            </div>

            <div className="px-6 pb-6 pt-0 flex flex-col-reverse items-stretch gap-3 mt-auto sm:flex-row sm:items-center sm:justify-end">
              {/* In-app add-to-calendar (QA F-A0-4): previously the .ics was
                  only reachable via the confirmation email's link. Same
                  endpoint, now surfaced on the page too. */}
              <Button
                variant="outline"
                asChild
                className="w-full sm:w-auto"
              >
                <a
                  href={`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/booking-ics?token=${encodeURIComponent(accessToken)}`}
                >
                  <CalendarPlus className="mr-2 h-4 w-4" />
                  Add to calendar
                </a>
              </Button>
              <Button onClick={() => navigate(businessSlug ? `/${businessSlug}` : "/")} className="w-full sm:w-auto">
                Back to home
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingConfirmed;
